/**
 * Time formatting utilities for consistent 12-hour AM/PM display
 *
 * Single source of truth for time/date/money formatting across the app.
 * Import from here — NEVER inline `.slice(0,5)` or `.substring(0,5)` on
 * time strings, and NEVER call `toLocaleTimeString` without `hour12: true`.
 */

/**
 * Convert a time string (HH:MM:SS or HH:MM) to 12-hour format
 * @param {string} timeStr - Time string like "14:30:00" or "09:00" or already "2:30 PM"
 * @returns {string} Formatted time like "2:30 PM" or "9:00 AM"
 */
export function formatTime12h(timeStr) {
  if (!timeStr) return 'TBD';  // Iter-336 — surface TBD instead of empty string
  // Already in 12h format (display-only string)
  if (typeof timeStr === 'string' && (timeStr.includes('AM') || timeStr.includes('PM'))) return timeStr;
  // Iter-223b — ISO 8601 timestamps (e.g. message.created_at) must go
  // through Date so we render in the user's BROWSER local timezone.
  // Previous code did a naive `.split(':')` on the ISO string which
  // parsed `2026-05-22T14` as hour=2026 → wrong time on every message.
  if (typeof timeStr === 'string' && /\d{4}-\d{2}-\d{2}T/.test(timeStr)) {
    const d = new Date(timeStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }
  // Fallback: assume "HH:MM" or "HH:MM:SS" wall-clock string.
  const parts = String(timeStr).split(':');
  let hours = parseInt(parts[0], 10);
  if (isNaN(hours)) return String(timeStr);
  const minutes = parts[1] || '00';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Format an ISO datetime string to show just the time in 12-hour format
 * @param {string} isoStr - ISO datetime string
 * @returns {string} Formatted time like "3:45 PM"
 */
export function formatDateTime12h(isoStr) {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * Iter-213+ — Timezone-aware variant of `formatDateTime12h`.
 * Renders an ISO datetime in the supplied IANA timezone (e.g. "America/New_York")
 * so sign-in / check-in / check-out times shown on operational sheets reflect
 * the VENUE/EVENT timezone rather than the viewer's browser. Falls back to
 * browser TZ when no timezone is provided so existing call sites don't regress.
 *
 * @param {string} isoStr - ISO datetime string (UTC)
 * @param {string} [tz]   - IANA timezone identifier (e.g. "America/Los_Angeles")
 * @returns {string} Formatted time like "3:45 PM"
 */
export function formatDateTime12hInTz(isoStr, tz) {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return '';
  const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
  if (tz) opts.timeZone = tz;
  try {
    return date.toLocaleTimeString('en-US', opts);
  } catch {
    // Bad tz string — fall back to browser tz so we never blow up the page.
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
}

/**
 * Format an ISO datetime string to show date and time.
 * Iter-225b — locked format: "MM-DD-YYYY · h:MM AM/PM"
 * @param {string} isoStr - ISO datetime string
 * @returns {string} Formatted like "03-14-2026 · 3:45 PM"
 */
export function formatFullDateTime12h(isoStr) {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${month}-${day}-${year} · ${time}`;
}

/**
 * Format a time range from start_time and end_time strings
 * @param {string} startTime - Start time like "14:00:00"
 * @param {string} endTime - End time like "22:00:00"
 * @returns {string} Formatted like "2:00 PM - 10:00 PM"
 */
export function formatTimeRange(startTime, endTime) {
  // Iter-336 — When either time is missing (nullable in DB when a shift is
  // TBD), render "Time: TBD" instead of the raw "Invalid Date - Invalid
  // Date" string every UI would otherwise show.
  if (!startTime || !endTime) return 'Time: TBD';
  return `${formatTime12h(startTime)} to ${formatTime12h(endTime)}`;
}

/**
 * Parse a date input safely, anchoring bare "YYYY-MM-DD" strings to local
 * noon so they don't drift to the previous day in negative-UTC timezones.
 * This is the single source of truth for date parsing — every formatter
 * below must go through this to prevent April 23 → April 22 style bugs.
 *
 * @param {string|Date} dateInput
 * @returns {Date|null}
 */
export function parseLocalDate(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return isNaN(dateInput.getTime()) ? null : dateInput;
  if (typeof dateInput !== 'string') return null;
  // Bare YYYY-MM-DD → treat as local noon (prevents UTC timezone day-shift)
  const bare = /^\d{4}-\d{2}-\d{2}$/.test(dateInput);
  const d = bare ? new Date(dateInput + 'T12:00:00') : new Date(dateInput);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date string to MM-DD-YYYY format (Iter-225b: dashes, locked).
 * Handles bare YYYY-MM-DD dates without UTC off-by-one drift.
 *
 * Iter-225b — Per user spec (2026-05-24) every user-facing date across
 * the platform (UI, PDFs, emails, exports) renders as MM-DD-YYYY with
 * dashes — e.g. "04-22-2026". This function used to emit slashes; the
 * function name is preserved for backward compatibility with the
 * ~25 call sites already importing it.
 *
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date like "04-22-2026"
 */
export function formatDateMMDDYYYY(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
}

/**
 * Iter-133t — dash-separated MM-DD-YYYY (per client preference).
 * Use this for every user-facing date column. Never render raw ISO
 * strings like "2026-04-26" in the UI.
 * @param {string|Date} dateInput
 * @returns {string} Formatted date like "04-26-2026"
 */
export function formatDate(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
}

/**
 * Iter-225b — full weekday + MM-DD-YYYY (dashes per locked spec).
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date like "Sunday, 04-26-2026"
 */
export function formatDateWithWeekday(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${weekday}, ${month}-${day}-${year}`;
}

/**
 * Format a date to short display format (Mon, Apr 22)
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date like "Mon, Apr 22"
 */
export function formatDateShort(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Format a date to full display format (Monday, April 22, 2026)
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date like "Monday, April 22, 2026"
 */
export function formatDateFull(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date range for multi-day shifts (Apr 20 – Apr 22).
 *
 * Iter-157h — defends against reversed inputs. Before this fix, a
 * shift saved with end_date < shift_date displayed "May 7 – May 6"
 * verbatim. We now collapse to the start date in that case so the
 * UI never renders a confusing reversed range. (The admin
 * create/edit form rejects reversed input on save, but old rows
 * exist in production.)
 *
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted range like "Apr 20 – Apr 22"
 */
export function formatDateRange(startDate, endDate) {
  const start = parseLocalDate(startDate);
  if (!start) return '';

  if (!endDate || startDate === endDate) {
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const end = parseLocalDate(endDate);
  if (!end) return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Reversed range — render as single-day so UX never says "May 7 – May 6".
  if (end.getTime() < start.getTime()) {
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  // Same day after parsing — same fallback.
  if (end.getTime() === start.getTime()) {
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return `${startStr} – ${endStr}`;
}


/**
 * Format a date to "Apr 22" style (month short + day, no year, no weekday).
 * Most common compact date format used in lists/cards.
 * @param {string|Date} dateInput
 * @returns {string} e.g. "Apr 22"
 */
export function formatDateMonthDay(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Render a Date as a bare "YYYY-MM-DD" string in LOCAL time (not UTC).
 *
 * Use this everywhere a calendar needs to compare "today" or a cell date
 * against a shift's bare shift_date. `new Date().toISOString().slice(0,10)`
 * returns the UTC date, which shifts by one day in negative-UTC timezones
 * (e.g. after 7pm EST "today" becomes tomorrow) — this is the root cause
 * of the April 22 → April 23 style drift in the client calendar.
 *
 * @param {Date} [d] - Date instance (defaults to now)
 * @returns {string} "YYYY-MM-DD" in local time
 */
export function toLocalYMD(d = new Date()) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Expand a multi-day shift into the list of bare YYYY-MM-DD dates it
 * occupies, inclusive of both endpoints. If `end_date` is missing or
 * equals `shift_date`, returns a single-element array. Dates are
 * generated in local time so they align with `toLocalYMD()` / the
 * calendar grid.
 *
 * @param {string} startYmd - bare "YYYY-MM-DD" for shift_date
 * @param {string|null} endYmd - bare "YYYY-MM-DD" for end_date
 * @returns {string[]} array of "YYYY-MM-DD" strings
 */
export function enumerateDates(startYmd, endYmd) {
  if (!startYmd) return [];
  if (!endYmd || endYmd === startYmd) return [startYmd];
  const start = parseLocalDate(startYmd);
  const end = parseLocalDate(endYmd);
  if (!start || !end || end < start) return [startYmd];
  const out = [];
  // Safety cap: never enumerate more than 60 days to guard against bad data
  for (let i = 0, cur = new Date(start); i < 60 && cur <= end; i++) {
    out.push(toLocalYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// -------------------------------------------------------------------------
// Aliases + currency — keep one consistent import site for the whole app
// -------------------------------------------------------------------------

/** Alias for `formatTime12h` — preferred name going forward. */
export const formatTime = formatTime12h;

/**
 * Format a USD amount in US locale, always with 2 decimals.
 * @param {number|string} amount
 * @returns {string} e.g. "$1,234.56"
 */
export function formatCurrency(amount) {
  const n = Number(amount || 0);
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// -------------------------------------------------------------------------
// Iter-133v — Event timezone label + shift date/time + timezone display
// -------------------------------------------------------------------------

/**
 * Map an IANA timezone like "America/Phoenix" to a friendly operator
 * label like "Arizona Time". Falls back to the IANA zone city if we
 * don't have a specific label, and to "Local Time" if the input is
 * empty/unknown.
 */
const TZ_LABELS = {
  'America/New_York':    'Eastern Time',
  'America/Detroit':     'Eastern Time',
  'America/Indiana/Indianapolis': 'Eastern Time',
  'America/Chicago':     'Central Time',
  'America/Denver':      'Mountain Time',
  'America/Phoenix':     'Arizona Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage':   'Alaska Time',
  'Pacific/Honolulu':    'Hawaii Time',
  'America/Boise':       'Mountain Time',
  'UTC':                 'UTC',
};

/**
 * Friendly label for an IANA timezone string.
 * @param {string|null|undefined} ianaTz
 * @returns {string} e.g. "Arizona Time" or "Local Time" if unknown
 */
export function timezoneLabel(ianaTz) {
  if (!ianaTz) return 'Local Time';
  if (TZ_LABELS[ianaTz]) return TZ_LABELS[ianaTz];
  // Fallback: take the city after the slash and insert spaces
  const parts = String(ianaTz).split('/');
  const city = (parts[parts.length - 1] || '').replace(/_/g, ' ');
  return city ? `${city} Time` : 'Local Time';
}

/**
 * US state → IANA timezone (mirrors backend utils/event_time.STATE_TO_TIMEZONE).
 * Used to resolve an event's timezone when `shifts.timezone` is null on older
 * rows. Keep in sync with the backend map.
 */
const STATE_TZ = {
  CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York', FL: 'America/New_York',
  GA: 'America/New_York', IN: 'America/New_York', KY: 'America/New_York', ME: 'America/New_York',
  MD: 'America/New_York', MA: 'America/New_York', MI: 'America/New_York', NH: 'America/New_York',
  NJ: 'America/New_York', NY: 'America/New_York', NC: 'America/New_York', OH: 'America/New_York',
  PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York', TN_ET: 'America/New_York',
  VT: 'America/New_York', VA: 'America/New_York', WV: 'America/New_York', PR: 'America/New_York',
  AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago', IA: 'America/Chicago',
  KS: 'America/Chicago', LA: 'America/Chicago', MN: 'America/Chicago', MS: 'America/Chicago',
  MO: 'America/Chicago', NE: 'America/Chicago', ND: 'America/Chicago', OK: 'America/Chicago',
  SD: 'America/Chicago', TX: 'America/Chicago', WI: 'America/Chicago', TN: 'America/Chicago',
  CO: 'America/Denver', ID: 'America/Denver', MT: 'America/Denver', NM: 'America/Denver',
  UT: 'America/Denver', WY: 'America/Denver',
  AZ: 'America/Phoenix',
  CA: 'America/Los_Angeles', NV: 'America/Los_Angeles', OR: 'America/Los_Angeles', WA: 'America/Los_Angeles',
  AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};

/**
 * Resolve the authoritative IANA timezone for an EVENT/shift. This is the
 * single source of truth the whole app must use to display actual clock
 * in/out times and scheduled times — never the browser or GPS timezone.
 *
 * Resolution order: explicit `shifts.timezone` → state map → America/New_York.
 *
 * @param {string|null|undefined} timezone  shifts.timezone (IANA)
 * @param {string|null|undefined} state     2-letter US state code (fallback)
 * @returns {string} IANA timezone name (always valid)
 */
export function resolveEventTz(timezone, state) {
  if (timezone && typeof timezone === 'string') return timezone;
  const code = (state || '').trim().toUpperCase();
  if (STATE_TZ[code]) return STATE_TZ[code];
  return 'America/New_York';
}

/**
 * Format a stored UTC clock timestamp (clock_in_time / clock_out_time) in the
 * EVENT'S timezone — the correct way to show "Actual" times. Falls back to a
 * dash when empty. GPS/browser timezone are intentionally ignored.
 *
 * @param {string} isoStr       UTC ISO timestamp
 * @param {string|null} timezone shifts.timezone
 * @param {string|null} state    shifts.state (fallback)
 * @param {string} empty         placeholder when no timestamp (default '—')
 */
export function formatClockTimeInEventTz(isoStr, timezone, state, empty = '—') {
  if (!isoStr) return empty;
  return formatDateTime12hInTz(isoStr, resolveEventTz(timezone, state));
}

/**
 * Offset (ms) between a timezone's local wall clock and UTC at a given instant.
 * offset = tzLocalWallClock - UTC. Positive east of UTC.
 */
export function tzOffsetMs(tz, date) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = dtf.formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return asUTC - date.getTime();
  } catch { return 0; }
}

/**
 * UTC ISO → "YYYY-MM-DDTHH:MM" wall-clock value in `tz` for a
 * <input type="datetime-local">. Empty string when no input.
 */
export function isoToTzInput(iso, tz) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const p = dtf.formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch { return ''; }
}

/**
 * datetime-local wall-clock value (interpreted as being in `tz`) → UTC ISO.
 * Two-pass offset resolution handles DST edges.
 */
export function tzInputToIso(v, tz) {
  if (!v) return null;
  const [dp, tp] = v.split('T');
  if (!dp || !tp) return null;
  const [y, mo, da] = dp.split('-').map(Number);
  const [h, mi] = tp.split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  let off = tzOffsetMs(tz, new Date(guess));
  let utc = guess - off;
  off = tzOffsetMs(tz, new Date(utc));
  utc = guess - off;
  return new Date(utc).toISOString();
}

/**
 * Iter-133v — canonical event-time display.
 *
 * Returns the shift date + start time in the EVENT'S own timezone
 * (so Arizona shifts always read "Arizona Time", never the viewer's
 * local). Intended to be shown on shift detail, rosters, and in
 * emails/SMS where the viewer could be anywhere.
 *
 *   formatEventDateTime('2026-07-19', '10:00:00', 'America/Phoenix')
 *     →  "07-19-2026 | 10:00 AM (Arizona Time)"
 *
 *   formatEventDateTime('2026-07-19', '10:00:00', null)
 *     →  "07-19-2026 | 10:00 AM (Local Time)"
 *
 * @param {string|Date} dateInput  bare YYYY-MM-DD or Date
 * @param {string} timeStr         "HH:MM[:SS]" local to the shift
 * @param {string|null} ianaTz     IANA timezone string
 * @returns {string}
 */
export function formatEventDateTime(dateInput, timeStr, ianaTz) {
  const date = formatLongDate(dateInput);
  const time = formatTime12h(timeStr);
  const abbr = tzAbbrev(ianaTz, null, dateInput);
  const timeWithTz = time && abbr ? `${time} ${abbr}` : time;
  if (!date && !timeWithTz) return '';
  if (!date) return timeWithTz;
  if (!timeWithTz) return date;
  return `${date}, ${timeWithTz}`;
}

/**
 * Event time-range display: "10:00 AM – 6:00 PM (Arizona Time)".
 * @param {string} startTime
 * @param {string} endTime
 * @param {string|null} ianaTz
 */
export function formatEventTimeRange(startTime, endTime, ianaTz) {
  const range = formatTimeRange(startTime, endTime);
  const abbr = tzAbbrev(ianaTz);
  return abbr ? `${range} ${abbr}` : range;
}

/* ------------------------------------------------------------------
 * 2026-06 — Canonical talent-facing event date/time
 *   "September 12, 2026, 10:00 AM to 6:00 PM EDT"
 * Always rendered in the EVENT's timezone, never the device's.
 * ------------------------------------------------------------------ */

export function formatLongDate(dateInput) {
  const date = parseLocalDate(dateInput);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function tzAbbrev(timezone, state, dateInput) {
  const tz = resolveEventTz(timezone, state);
  if (!tz) return '';
  try {
    const when = parseLocalDate(dateInput) || new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short', hour: 'numeric',
    }).formatToParts(when);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

export function formatEventWhen(dateInput, startTime, endTime, timezone, state) {
  const datePart = formatLongDate(dateInput);
  let timePart = formatTimeRange(startTime, endTime);
  if (timePart) {
    const abbr = tzAbbrev(timezone, state, dateInput);
    if (abbr) timePart = `${timePart} ${abbr}`;
  }
  if (datePart && timePart) return `${datePart}, ${timePart}`;
  return datePart || timePart || '';
}
